import React, { useState } from 'react';
import { Grid, useMediaQuery } from '@mui/material';
import { PrimaryButton, Text, SizeableRoundedDialog, UnderlinedTextInput } from 'library';
import theme from 'theme';
import useLoading from 'library/hooks/useLoading';
import { client } from 'shared';
import { ShiftProviderUser } from 'models';

interface RevealData {
    full_name: string;
    ssn: string;
    dob: string;
    place_of_birth: string;
    address: {
        street: string;
        city: string;
        state: string;
        zip: string;
    };
    phone: string;
    email: string;
    sex: string;
    race: string;
    height: number;
    weight: number;
    hair_color: string;
    eye_color: string;
    prior_states: string[];
}

const DisplayData = ({ data }: { data: RevealData }) => {
    return (
        <Grid item container direction="column" style={{ gap: 8 }}>
            <Text>{data.full_name}</Text>
            <Text>DOB: {data.dob}</Text>
            <Text>SSN: {data.ssn}</Text>
            <Text>Place of Birth: {data.place_of_birth}</Text>
            <Text>
                Address: {data.address.street}, {data.address.city}, {data.address.state} {data.address.zip}
            </Text>
            <Text>Phone: {data.phone}</Text>
            <Text>Email: {data.email}</Text>
            <Text>Sex: {data.sex}</Text>
            <Text>Race: {data.race}</Text>
            <Text>Height: {data.height}</Text>
            <Text>Weight: {data.weight}</Text>
            <Text>Hair Color: {data.hair_color}</Text>
            <Text>Eye Color: {data.eye_color}</Text>
            <Text>States Resided In Past 5 Years: {data.prior_states.join(', ')}</Text>
        </Grid>
    );
};

export default function RevealModal({
    open,
    onClose,
    provider,
}: {
    open: boolean;
    onClose: () => void;
    provider: ShiftProviderUser;
}) {
    const small = useMediaQuery(theme.breakpoints.down('md'));
    const { loading, setLoading } = useLoading();
    const [code, setCode] = useState<string>('');
    const [data, setData] = useState<RevealData>();
    const [error, setError] = useState<string>();
    function submit() {
        setError(undefined);
        setLoading(true);
        client(`api/business/reveal/`, { method: 'POST', body: { provider: provider.id, code: code } })
            .then((response) => {
                setData(response);
            })
            .catch((error) => {
                setError(error['error']);
            })
            .finally(() => {
                setLoading(false);
            });
    }
    const title = `Reveal ${provider.first_name} ${provider.last_name}'s SSN?`;
    const body =
        "You will receive a text message with a code to access the provider's SSN. If your number cannot receive texts you will get an email. Please enter the code here. Kindly note that this information is for roster purposes only.";
    return (
        <SizeableRoundedDialog
            open={open}
            onClose={() => {
                setData(undefined);
                onClose();
            }}
            fullScreen={small}
            closeButton
            maxWidth="md"
            style={{ maxWidth: 500 }}
        >
            <Grid container item direction="column" style={{ padding: 20, gap: 10 }}>
                <Text variant="h1">{title}</Text>
                <Text>{body}</Text>
                {!data && (
                    <UnderlinedTextInput
                        label="Code"
                        value={code}
                        onChange={(value: string) => {
                            setCode(value);
                        }}
                        error={false}
                        errorText={''}
                    />
                )}
                {data ? (
                    <DisplayData data={data} />
                ) : (
                    <PrimaryButton
                        disabled={loading}
                        onClick={submit}
                        buttonStyle={{ width: undefined, alignSelf: 'flex-end' }}
                    >
                        Reveal
                    </PrimaryButton>
                )}
                {error && <Text>{error}</Text>}
            </Grid>
        </SizeableRoundedDialog>
    );
}
